import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Heart, Star, BookOpen } from "lucide-react";
import EmptyState from "./EmptyState";
import { useAuth } from "../../context/AuthContext";

const PostsGridSection = ({
  filteredPosts,
  openModal,
  typeIcons,
  activeTab,
  onCreatePost,
}) => {
  const navigate = useNavigate();
  const { username } = useParams();
  const { user: loggedInUser } = useAuth();

  // Helper array for types that use chapters
  const chapterBasedTypes = ["Novel", "Book", "Story"];

  const handlePostClick = (postId) => {
    if (activeTab && activeTab !== "all") {
      navigate(`/p/${postId}?type=${activeTab}`, {
        state: { postType: activeTab },
      });
    } else {
      navigate(`/p/${postId}`);
    }
  };

  // Helper function to check if viewing own profile
  const isOwnProfile = () => {
    const profileUsername = username?.replace("@", "") || "";
    const currentUsername = loggedInUser?.profile?.username || "";
    return profileUsername.toLowerCase() === currentUsername.toLowerCase();
  };

  return (
    <div className="w-full px-1 xs:px-2 sm:px-4 md:px-6 py-2 xs:py-4 sm:py-6 md:py-8">
      <div className="grid grid-cols-3 gap-0.5 xs:gap-1 sm:gap-2 md:gap-3 lg:gap-4">
        {filteredPosts.map((post) => {
          const IconComponent = typeIcons[post.type] || BookOpen;
          return (
            <div
              key={post.id}
              onClick={() => handlePostClick(post.id)}
              className="aspect-square relative group cursor-pointer overflow-hidden bg-white rounded-sm xs:rounded md:rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
            >
              {/* Background */}
              <div className={`absolute inset-0 ${post.backgroundStyle}`} />

              {/* Conditional Content or Image */}
              {(post.content && post.content.trim() !== "") ||
              (chapterBasedTypes.includes(post.type) &&
                post.chapters?.length > 0) ||
              (post.type === "Script" && post.scenes?.length > 0) ? (
                // Content Layout
                <div className="absolute inset-0 p-1 xs:p-2 sm:p-3 md:p-4 flex flex-col justify-between">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="w-5 h-5 xs:w-5 xs:h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center p-1 bg-gray-200 border border-black xs:border md:border-2 md:border-black">
                      <IconComponent className="w-3 h-3 md:w-4 md:h-4 text-black" />
                    </div>
                    {post.featured && (
                      <div className="w-3 h-3 xs:w-4 xs:h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                        <Star className="w-1.5 h-1.5 xs:w-2 xs:h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 text-yellow-800" />
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div className="flex-1 flex items-center justify-center px-0.5 xs:px-1 sm:px-2">
                    <div className="text-center">
                      <h3
                        className={`text-center font-semibold leading-tight text-xs xs:text-sm sm:text-base md:text-lg lg:text-xl ${
                          post.textColor === "white"
                            ? "text-white"
                            : "text-gray-900"
                        } ${post.fontStyle}`}
                        style={{
                          fontSize: "clamp(0.65rem, 2.5vw, 1.25rem)",
                          lineHeight: "1.2",
                        }}
                      >
                        {post.title}
                      </h3>
                      {/* Show chapter count for chapter-based types */}
                      {chapterBasedTypes.includes(post.type) &&
                        post.chapters?.length > 0 && (
                          <p
                            className={`text-xs ${
                              post.textColor === "white"
                                ? "text-gray-300"
                                : "text-gray-600"
                            }`}
                            style={{
                              fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)",
                            }}
                          >
                            {post.chapters.length}{" "}
                            {post.chapters.length === 1
                              ? "Chapter"
                              : "Chapters"}
                          </p>
                        )}
                      {/* Show scene count for Script */}
                      {post.type === "Script" && post.scenes?.length > 0 && (
                        <p
                          className={`text-xs ${
                            post.textColor === "white"
                              ? "text-gray-300"
                              : "text-gray-600"
                          }`}
                          style={{ fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)" }}
                        >
                          {post.scenes.length}{" "}
                          {post.scenes.length === 1 ? "Scene" : "Scenes"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <div
                      className="flex items-center gap-0.5 xs:gap-1 text-xs xs:text-xs sm:text-sm text-gray-900"
                      style={{ fontSize: "clamp(0.5rem, 1.8vw, 0.75rem)" }}
                    >
                      <Heart
                        className="w-2 h-2 xs:w-2.5 xs:h-2.5 sm:w-3 sm:h-3"
                        fill={post.isLikedByCurrentUser ? "#ef4444" : "none"}
                        color={
                          post.isLikedByCurrentUser ? "#ef4444" : "currentColor"
                        }
                      />
                      <span>{post.likes}</span>
                    </div>
                    <div
                      className="text-gray-900 drop-shadow-md text-xs xs:text-xs sm:text-sm text-gray-500"
                      style={{ fontSize: "clamp(0.5rem, 1.8vw, 0.75rem)" }}
                    >
                      {formatPostDate(post.publishedAt)}
                    </div>
                  </div>
                </div>
              ) : (
                // Image Layout (remains unchanged)
                <div className="absolute inset-0">
                  {/* Image */}
                  <img
                    src={post.imageurl}
                    alt={post.title}
                    className="w-full h-full object-contain"
                  />

                  {/* Overlay with post info */}
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 p-1 xs:p-2 sm:p-3 md:p-4 flex flex-col justify-between">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        {post.featured && (
                          <div className="w-3 h-3 xs:w-4 xs:h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                            <Star className="w-1.5 h-1.5 xs:w-2 xs:h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 text-yellow-800" />
                          </div>
                        )}
                      </div>

                      {/* Title */}
                      <div className="flex-1 flex items-end justify-center px-0.5 xs:px-1 sm:px-2">
                        <h3
                          className="text-center font-bold leading-tight text-white drop-shadow-lg text-xs xs:text-sm sm:text-base md:text-lg lg:text-xl"
                          style={{
                            fontSize: "clamp(0.65rem, 2.5vw, 1.25rem)",
                            lineHeight: "1.2",
                          }}
                        >
                          {post.title}
                        </h3>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between">
                        <div
                          className={`flex items-center gap-0.5 xs:gap-1 text-xs xs:text-xs sm:text-sm px-1.5 py-0.5 rounded drop-shadow-md ${
                            post.isLikedByCurrentUser
                              ? "bg-red-500 text-white"
                              : "text-gray-900"
                          }`}
                          style={{ fontSize: "clamp(0.5rem, 1.8vw, 0.75rem)" }}
                        >
                          <Heart className="w-2 h-2 xs:w-2.5 xs:h-2.5 sm:w-3 sm:h-3" />
                          <span>{post.likes}</span>
                        </div>
                        <div
                          className="text-gray-900 drop-shadow-md text-xs xs:text-xs sm:text-sm text-gray-500"
                          style={{ fontSize: "clamp(0.5rem, 1.8vw, 0.75rem)" }}
                        >
                          {formatPostDate(post.publishedAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredPosts.length === 0 && (
        <EmptyState
          activeTab={activeTab}
          onCreatePost={onCreatePost}
          isOwnProfile={isOwnProfile()}
        />
      )}
    </div>
  );
};

const formatPostDate = (date) => {
  if (!date) return "";

  const postDate = new Date(date);
  return postDate.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default PostsGridSection;